//@ts-nocheck

'use client'

import React from 'react'

import Button from '../../atoms/Button/Button.component'

import * as S from './Box.styles'

import { Paragraph } from '../../atoms/Typography/Typography.component'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import normalizeImage from '../../../models/base/ImageBase'
import { getColorByScheme } from '@caffeina-siad/utils/src/get-color-by-scheme'
import Image from 'next/image'
import { useContext } from 'react'
import Shape from '../../atoms/Shape/Shape.component'
import Icon from '../../atoms/Icon/Icon.component'
import { SpacerBase } from '../../atoms/GridSystem'

const ALIGN_OPTIONS = { LEFT: 'left', CENTER: 'center' } as const
type AlignmentType = typeof ALIGN_OPTIONS[keyof typeof ALIGN_OPTIONS]

interface ListItem {
  id?: string | number
  element: string
}

interface ImageType {
  src: string
  alt: string
  url?: string
}

interface CTAType {
  label: string
  [key: string]: any
}

interface TitleType {
  [key: string]: any
}

interface BoxProps {
  alignment?: AlignmentType
  colorScheme?: string | null
  preTitle?: string | null
  title?: TitleType | null
  subtitle?: TitleType
  lists?: ListItem[]
  boxLength?: number
  ctaPrimary?: CTAType
  ctaTertiary?: CTAType
  image?: ImageType | null
  imageShape?: string
  withImage?: boolean
  isBentoBox?: boolean
  bentosCardAlignment?: string
}

const Box: React.FC<BoxProps> = ({
  alignment: _alignment = ALIGN_OPTIONS.CENTER,
  colorScheme: _colorScheme = null,
  preTitle = null,
  title: _title = null,
  subtitle: _subtitle,
  lists,
  boxLength,
  ctaPrimary,
  ctaTertiary,
  image: _image = null,
  imageShape,
  withImage: _withImage = false,
  isBentoBox = false,
  bentosCardAlignment,
}) => {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const colorScheme = _colorScheme || selectedColorScheme
  const alignment = Object.values(ALIGN_OPTIONS).includes(_alignment) ? _alignment : ALIGN_OPTIONS.CENTER
  const withImage = _withImage && _image && !!_image?.url
  const title = _title && Object.keys(_title).length !== 0 ? _title : null
  const subtitle = _subtitle && Object.keys(_subtitle).length !== 0 ? _subtitle : null
  const image = _image ? normalizeImage(_image) : null

  return (
    <S.BoxWrapper
      colorScheme={colorScheme}
      $withImage={withImage}
      $alignment={alignment}
      $isBentoBox={isBentoBox}
      className="box-wrapper"
      variant={bentosCardAlignment}
    >
      <S.BoxContent
        $alignment={alignment}
        $withImage={withImage}
        $isBentoBox={isBentoBox}
      >
        <S.BoxTextContent $alignment={alignment}>
          {preTitle ? (
            <Paragraph
              typo="label"
              content={preTitle}
              weight="medium"
              uppercase
            />
          ) : null}
          {title ? (
            <>
              <Paragraph {...title} weight="medium" />
              <SpacerBase xs={1} />
            </>
          ) : null}
          {subtitle ? (
            <S.BoxTextSecondary colorScheme={colorScheme}>
              <Paragraph {...subtitle} as="div" />
              <SpacerBase xs={1.5} />
            </S.BoxTextSecondary>
          ) : null}
        </S.BoxTextContent>
        {lists && lists.length > 0 ? (
          <S.BoxFeaturesList>
            {lists.map((item, i) => (
              <S.BoxFeaturesListItem
                key={`BoxListItem-${i}-${item?.id}`}
              >
                <Icon name="check" />
                <Paragraph typo="paragraphMD">{item.element}</Paragraph>
              </S.BoxFeaturesListItem>
            ))}
          </S.BoxFeaturesList>
        ) : null}
        {ctaPrimary || ctaTertiary ? (
          <S.BoxActions>
            {ctaPrimary && ctaPrimary?.label ? (
              <S.BoxButtonWrapper>
                <Button
                  {...ctaPrimary}
                  variant="primary"
                  iconEnd="arrow-right"
                  inheritedColorScheme={colorScheme}
                />
              </S.BoxButtonWrapper>
            ) : null}
            {ctaTertiary && ctaTertiary?.label ? (
              <S.BoxButtonWrapper>
                <Button
                  {...ctaTertiary}
                  variant="secondary"
                  iconEnd="arrow-right"
                  inheritedColorScheme={colorScheme}
                />
              </S.BoxButtonWrapper>
            ) : null}
          </S.BoxActions>
        ) : null}
        {!withImage ? (
          <S.BoxShape $isBentoBox={isBentoBox} className="bento-box-shape">
            <Shape
              $imageShape={imageShape}
              color={getColorByScheme(colorScheme)}
            />
          </S.BoxShape>
        ) : null}
      </S.BoxContent>
      {withImage && image?.src ? (
        <S.BoxImageWrapper>
          <Image
            src={image?.src}
            alt={image?.alt || ''}
            fill
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 50vw, 75vw"
          />
        </S.BoxImageWrapper>
      ) : null}
    </S.BoxWrapper>
  )
}

export default Box
